import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { ParkingLotFormComponent } from "@onsip/web/features/administrators/parkingLot/parking-lot-form.component";
import { AdvParkService } from "@onsip/common/services/api/resources/parkingLot/parking-lot.service";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, of, Subscription } from "rxjs";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { map, take } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { Router } from "@angular/router";
import { DynamicPricingService } from "@onsip/common/services/dynamic-pricing.service";
import { MatStepper } from "@angular/material/stepper";
import { STEPPER_GLOBAL_OPTIONS } from "@angular/cdk/stepper";
import { MusicSourceService } from "@onsip/common/services/api/resources/musicSource/music-source.service";
import { MusicSource } from "@onsip/common/services/api/resources/musicSource/music-source";
import {
  AddressSummary,
  AddressSummaryService
} from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { AdvParkingLotParams } from "@onsip/common/services/api/resources/parkingLot/parking-lot";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { AppAddressService } from "@onsip/common/services/api/resources/appAddress/app-address.service";
import { AppAddress } from "@onsip/common/services/api/resources/appAddress/app-address";
import {
  UserAlias,
  UserAliasService
} from "@onsip/common/services/api/resources/userAlias/user-alias.service";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { NonNullableFormBuilder } from "@angular/forms";
import { UserPackagePlan } from "@onsip/common/services/api/resources/package/package";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";

@Component({
  selector: "onsip-parking-lot-setup",
  templateUrl: "./parking-lot-setup.component.html",
  styleUrls: ["./parking-lot-setup.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    }
  ],
  standalone: false
})
export class ParkingLotSetupComponent extends ParkingLotFormComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-admin-host-grid")
  @HostBinding("class.onsip-grid-content")
  _dontUse = true;
  @ViewChild("stepper") stepper!: MatStepper;
  E2eLocators = E2eLocators;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private dynamicPricing: DynamicPricingService,
    private addressSummaryService: AddressSummaryService,
    private snackbar: SnackbarService,
    private appAddressService: AppAddressService,
    private userAlias: UserAliasService,
    protected translate: TranslateService,
    protected parkLotService: AdvParkService,
    protected musicSourceService: MusicSourceService,
    protected dialog: MatDialog,
    protected fb: NonNullableFormBuilder,
    protected userService: UserService
  ) {
    super(translate, parkLotService, musicSourceService, dialog, fb, userService);
  }
  mobileView!: Observable<boolean>;
  tableView!: Observable<boolean>;
  unsubscriber = new Subscription();
  pricingText = "";
  edit = false;
  musicSources!: Array<MusicSource>;
  addressSummaries!: Array<AddressSummary>;
  failoverName!: string;
  domain!: string;
  loadingStateComplete$ = new BehaviorSubject(true);
  private formValueSnapshot = this.parkLotForm.getRawValue();
  private readonly minStarCode = 44;
  private packagePlan: UserPackagePlan | undefined;

  ngOnInit(): void {
    this.addressSummaryService.addressSummaryBrowse();
    this.musicSourceService.musicSourceBrowse();
    this.userAlias.userAliasBrowse();
    this.getFeaturePrice();

    this.mobileView = this.breakpointObserver
      .observe([Breakpoints.XSmall])
      .pipe(map(res => res.matches));
    this.tableView = this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.Medium])
      .pipe(map(res => res.matches));

    this.parkLotForm.patchValue({
      channelLimit: this.defaultChannelLimit
    });

    this.initSubscriptions();
    this.setPricing();
  }

  getFeaturePrice(): void {
    this.unsubscriber.add(
      this.dynamicPricing.getPackageInfo$("AdvParkChannel").subscribe(res => {
        this.packagePlan = res?.packagePlan;
        if (res && res.featurePricing) {
          this.featurePricing = res.featurePricing;
          this.calculateCurrentPrice(this.defaultChannelLimit);
        }
      })
    );
  }

  private setPricing(): void {
    this.dynamicPricing.getDynamicPricing("AdvParkChannel", false).then(result => {
      this.pricingText = result;
      this.cdRef.markForCheck();
    });
  }

  private initSubscriptions(): void {
    this.unsubscriber.add(
      this.addressSummaryService.orgState.subscribe(addressSummaries => {
        this.addressSummaries = addressSummaries;
        this.domain = addressSummaries[0]?.domain;
      })
    );

    this.unsubscriber.add(
      this.userAlias.orgState.pipe(take(1)).subscribe(aliases => {
        this.getUsedStarCodes(aliases);
      })
    );

    this.unsubscriber.add(
      this.parkLotForm.controls.name.valueChanges.subscribe(value => {
        this.parkLotForm.patchValue({
          parkSipAddress: `${value}${this.parkSuffix}`,
          retrieveSipAddress: `${value}${this.retrieveSuffix}`
        });
      })
    );

    this.unsubscriber.add(
      this.musicSources$.subscribe(res => {
        this.musicSources = res;
        if (this.musicSources.length) {
          const defMusic = this.musicSources.find(el => el.default);
          this.parkLotForm.patchValue({
            musicSourceId: defMusic?.musicSourceId
          });
        }
      })
    );

    this.unsubscriber.add(
      this.parkLotForm.controls.musicSourceId.valueChanges.subscribe(value => {
        this.parkLotForm.patchValue({
          musicSourceName: this.musicSources.find(el => el.musicSourceId === value)?.name
        });
      })
    );

    this.unsubscriber.add(
      this.parkLotForm.controls.pin.valueChanges.subscribe(value => {
        this.parkLotForm.patchValue({
          pinRetrievalEnable: !!value
        });
      })
    );

    this.unsubscriber.add(
      this.parkLotForm.controls.manualReturnDtmf.valueChanges.subscribe(value => {
        this.parkLotForm.patchValue({
          manualReturnEnable: !!value
        });
      })
    );

    this.unsubscriber.add(
      this.parkLotForm.controls.returnAddress.valueChanges.subscribe(value => {
        const failover = this.addressSummaries?.find(el => el.aor === value);
        this.failoverName = failover ? failover.name : value;
      })
    );

    this.initChannelLimitSubscription();
  }

  private getUsedStarCodes(aliases: Array<UserAlias>): void {
    const usedStarCodes = aliases
      .filter(
        el =>
          el.aliasUsername.includes("*") && Number(el.aliasUsername.slice(1)) > this.minStarCode - 1
      )
      .map(el => Number(el.aliasUsername.slice(1)))
      .sort((a, b) => {
        return a - b;
      });
    if (!usedStarCodes.length) {
      this.setStarCodeToExt("extensions", this.minStarCode);
      this.setStarCodeToExt("retrieverExtensions", this.minStarCode + 1);
      return;
    }
    this.defineAvailableStarCode(usedStarCodes);
  }

  private defineAvailableStarCode(usedStarCodes: Array<number>): void {
    const maxStarCode = 99999;
    const res: Array<number> = [];
    const maxUsedStarCode = Math.max(...usedStarCodes);
    for (let i = this.minStarCode; i <= maxUsedStarCode + 1; i++) {
      if (i === maxStarCode) break;
      if (!usedStarCodes.includes(i) && !usedStarCodes.includes(i + 1)) {
        res.push(...[i, i + 1]);
        break;
      }
    }
    if (res.length < 2) {
      this.snackbar.openSnackBar(
        this.translate.instant("ONSIP_I18N.ALL_STAR_CODES_ARE_IN_USE"),
        "error"
      );
    }
    if (res[0]) {
      this.setStarCodeToExt("extensions", res[0]);
    }
    if (res[1]) {
      this.setStarCodeToExt("retrieverExtensions", res[1]);
    }
  }

  private setStarCodeToExt(
    extensions: "extensions" | "retrieverExtensions",
    starCode: number
  ): void {
    this.parkLotForm.controls[extensions].patchValue([`*${starCode}`]);
  }

  toggleEditInfo(): void {
    if (!this.edit) {
      this.edit = !this.edit;
      this.formValueSnapshot = {
        ...this.parkLotForm.getRawValue(),
        extensions: [...this.parkLotForm.controls.extensions.value],
        retrieverExtensions: [...this.parkLotForm.controls.retrieverExtensions.value]
      };
    } else {
      const modal = this.dialog.open(ModalMaterialComponent, this.cancelDialogOptions);

      this.unsubscriber.add(
        modal
          .afterClosed()
          .pipe(take(1))
          .subscribe(retObj => {
            if (retObj && retObj.doPrimaryAction) {
              this.parkLotForm.patchValue({ ...this.formValueSnapshot });
              this.edit = !this.edit;
            }
          })
      );
    }
  }

  save(): void {
    if (this.parkLotForm.invalid) {
      return;
    }
    this.loadingStateComplete$.next(false);
    const sipAddressWithoutSuffix = this.removeSipAddressSuffix(
      this.parkLotForm.controls.parkSipAddress.value
    );
    const returnAddress = this.parkLotForm.controls.returnAddress.value;
    const params: AdvParkingLotParams = {
      Name: this.parkLotForm.controls.name.value,
      Username: sipAddressWithoutSuffix,
      UsernameRetriever: this.parkLotForm.controls.retrieveSipAddress.value,
      ChannelLimit: this.parkLotForm.controls.channelLimit.value,
      AutoReturnTimeout: this.parkLotForm.controls.autoReturnTimeout.value,
      ManualReturnDtmf: this.parkLotForm.controls.manualReturnDtmf.value,
      ManualReturnEnable: this.parkLotForm.controls.manualReturnEnable.value,
      MusicSourceId: this.parkLotForm.controls.musicSourceId.value,
      Pin: this.parkLotForm.controls.pin.value,
      PinRetrievalEnable: this.parkLotForm.controls.pinRetrievalEnable.value,
      ...(returnAddress && { ReturnAddress: returnAddress })
    };

    this.parkLotService.advParkAdd(params).then(res => {
      if (res && res.status === "success") {
        const aor = sipAddressWithoutSuffix + "@" + this.domain;
        this.parkSipAddress.updateAppAddress(aor).then(res => {
          if (res && res.status === "success") {
            this.extensionsListRef.editExtensions(this.parkLotForm.controls.parkSipAddress.value);
          }

          this.snackbar.openSnackBar("Parking lot successfully created", "success");
          this.router.navigate([this.views.ADMIN_PARKING_LOT]);
        });

        if (
          aor
            .replace(this.parkSuffix, "")
            .startsWith(
              this.parkLotForm.controls.retrieveSipAddress.value.replace(this.retrieveSuffix, "")
            )
        ) {
          this.retrieverExtensionsListRef.editExtensions(
            this.parkLotForm.controls.retrieveSipAddress.value
          );
        } else {
          const retrieveAddressId = res.data[Object.keys(res.data)[0]].advParkRetrieverAppId;
          this.appAddressService.appAddressBrowse().then(response => {
            const retrieveAddress: AppAddress = Object.values(response.data).find(
              ({ appId }) => appId === retrieveAddressId
            );

            this.retrieverSipAddressField
              .updateAppAddress(
                `${retrieveAddress.address.username}@${retrieveAddress.address.domain}`
              )
              .then(() => {
                this.retrieverExtensionsListRef.editExtensions(
                  this.parkLotForm.controls.retrieveSipAddress.value
                );
              });
          });
        }
      } else {
        this.snackbar.openSnackBar(res.data.message || "Parking lot was not created", "error");
        this.parkLotService.clearErrors();
      }
      this.loadingStateComplete$.next(true);
    });
  }

  removeSipAddressSuffix(sipAddress: string): string {
    return sipAddress.replace(this.parkSuffix, "");
  }

  cancel() {
    const modal = this.dialog.open(ModalMaterialComponent, this.cancelDialogOptions);

    this.unsubscriber.add(
      modal
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.router.navigate([this.views.ADMIN_PARKING_LOT]);
          }
        })
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  prevStep(): void {
    this.stepper.previous();
  }

  goToReview(): void {
    if (this.parkLotForm.invalid) return;
    this.stepper.next();
  }

  goToCallerSettings(): void {
    //required controls on this step: name, sipAddress, retrieveSipAddress
    const nameControl = this.parkLotForm.controls.name;
    const parkSipAddressControl = this.parkLotForm.controls.parkSipAddress;
    const retrieveSipAddressControl = this.parkLotForm.controls.retrieveSipAddress;
    if (nameControl.valid && parkSipAddressControl.valid && retrieveSipAddressControl.valid) {
      if (this.packagePlan === UserPackagePlan.UNLIMITED) {
        if (this.stepper.selected) this.stepper.selected.completed = true;
        this.stepper.next();
        return;
      }
      this.unsubscriber.add(
        this.userService.selfUserIsAccountAdmin$
          .pipe(
            map(isAccountAdmin => {
              if (isAccountAdmin) {
                return this.showChannelsConfirmation().pipe(take(1));
              } else {
                return of(true);
              }
            })
          )
          .subscribe(res => {
            if (res) {
              if (this.stepper.selected) this.stepper.selected.completed = true;
              this.stepper.next();
            }
          })
      );
    } else {
      nameControl.markAllAsTouched();
      parkSipAddressControl.markAllAsTouched();
      retrieveSipAddressControl.markAllAsTouched();
    }
  }
}
